import {startTransition, useCallback, useEffect, useMemo, useState} from 'react';
import {Box, Button, Skeleton, Stack, Typography} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import {SplitTabsPanel} from '@/components/common';
import {
    AuditProgramSectionVO,
    getFSectionVO,
    saveFSectionPage,
} from '@/services/auditService/auditProgramService';
import {
    F2_PAGE,
    FAPP01_PAGE,
    FS_PAGE,
    F_PAGE_TITLES,
    F_SECTION_INDEX,
    F_SECTION_INDEX_LETTER,
    F_SECTION_INDEX_TITLE,
    F_SECTION_PAGES,
    FProgrammePage,
    FSectionPageCode,
    FSummaryPage,
} from '@/pages/components/audit-program/f-section/constants';
import FsSummaryView from '@/pages/components/audit-program/f-section/FsSummaryView';
import G0ProgrammeView from '@/pages/components/audit-program/g-section/G0ProgrammeView';
import PageTabLabel from '@/pages/components/audit-program/PageTabLabel';
import SectionIndexView from '@/pages/components/audit-program/SectionIndexView';
import {useProgramSectionTemplates} from '@/pages/components/audit-program/templates/useProgramSectionTemplates';

export type FSectionViewProps = {
    companyId: number;
    annualId: number;
    companyName?: string | null;
    initialPage?: FSectionPageCode;
};

const DISPLAY_ONLY_PAGES = new Set<FSectionPageCode>(['Fi']);

function toStringRecord(data?: Record<string, unknown>): Record<string, string> {
    if (!data) return {};
    return Object.fromEntries(
        Object.entries(data).map(([k, v]) => [k, v == null ? '' : String(v)]),
    );
}

function withHeaderDefaults(
    page: Record<string, string>,
    data: AuditProgramSectionVO,
    refCode: string,
): Record<string, string> {
    return {
        ...page,
        client: page.client || data.client || '',
        yearEnd: page.yearEnd || data.yearEnd || '',
        fileNo: page.fileNo || data.fileNo || '',
        preparedBy: page.preparedBy || data.preparedInitial || data.preparedBy || '',
        completedBy: page.completedBy || data.preparedInitial || data.preparedBy || '',
        reviewedBy: page.reviewedBy || data.reviewedInitial || data.reviewedBy || '',
        ref: page.ref || refCode,
    };
}

function isProgramme(code: FSectionPageCode): boolean {
    return code === 'F2' || code === 'Fapp01';
}

export default function FSectionView({
    companyId,
    annualId,
    companyName,
    initialPage = 'Fi',
}: FSectionViewProps) {
    const [pageKey, setPageKey] = useState<FSectionPageCode>(initialPage);
    const [header, setHeader] = useState<AuditProgramSectionVO | null>(null);
    const [pageData, setPageData] = useState<Record<string, Record<string, string>>>({});
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [dirty, setDirty] = useState<Record<string, boolean>>({});
    const {getContent} = useProgramSectionTemplates('F');

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const data = await getFSectionVO(companyId, annualId);
            if (companyName && !data.client) {
                data.client = companyName;
            }
            setHeader(data);
            const pages: Record<string, Record<string, string>> = {};
            for (const code of F_SECTION_PAGES) {
                if (code === 'Fi') {
                    pages[code] = toStringRecord(data.pages?.[code]);
                    continue;
                }
                pages[code] = withHeaderDefaults(toStringRecord(data.pages?.[code]), data, code);
            }
            setPageData(pages);
            setDirty({});
        } catch (e: unknown) {
            const msg = e instanceof Error ? e.message : 'Failed to load F Section';
            setError(msg);
            setHeader(null);
        } finally {
            setLoading(false);
        }
    }, [companyId, annualId, companyName]);

    useEffect(() => {
        void load();
    }, [load]);

    useEffect(() => {
        setPageKey(initialPage);
    }, [initialPage]);

    const handleFieldChange = useCallback((pageCode: FSectionPageCode, key: string, value: string) => {
        setPageData((prev) => ({
            ...prev,
            [pageCode]: {...(prev[pageCode] ?? {}), [key]: value},
        }));
        setDirty((prev) => ({...prev, [pageCode]: true}));
    }, []);

    const handleSave = useCallback(async () => {
        if (DISPLAY_ONLY_PAGES.has(pageKey) || !dirty[pageKey]) return;
        setSaving(true);
        setError(null);
        try {
            await saveFSectionPage(companyId, annualId, pageKey, pageData[pageKey] ?? {});
            setDirty((prev) => ({...prev, [pageKey]: false}));
        } catch (e: unknown) {
            const msg = e instanceof Error ? e.message : 'Failed to save';
            setError(msg);
        } finally {
            setSaving(false);
        }
    }, [companyId, annualId, dirty, pageKey, pageData]);

    const indexRows = useMemo(() => {
        const fromApi = getContent('Fi') as {rows?: typeof F_SECTION_INDEX} | undefined;
        return fromApi?.rows?.length ? fromApi.rows : F_SECTION_INDEX;
    }, [getContent]);

    const fsContent = (getContent('Fs') ?? FS_PAGE) as FSummaryPage;
    const f2Content = (getContent('F2') ?? F2_PAGE) as FProgrammePage;
    const fapp01Content = (getContent('Fapp01') ?? FAPP01_PAGE) as FProgrammePage;

    const tabs = useMemo(() => F_SECTION_PAGES.map((code) => ({
        key: code,
        label: <PageTabLabel code={code} title={F_PAGE_TITLES[code]}/>,
        icon: code,
        content: header ? (
            code === 'Fi' ? (
                <SectionIndexView
                    header={header}
                    refCode="Fi"
                    sectionLetter={F_SECTION_INDEX_LETTER}
                    sectionTitle={F_SECTION_INDEX_TITLE}
                    rows={indexRows}
                    onNavigate={(pageCode) => startTransition(() => setPageKey(pageCode as FSectionPageCode))}
                />
            ) : code === 'Fs' ? (
                <FsSummaryView
                    header={header}
                    page={fsContent}
                    refCode="Fs"
                    values={pageData.Fs ?? {}}
                    onChange={(k, v) => handleFieldChange('Fs', k, v)}
                />
            ) : isProgramme(code) ? (
                <G0ProgrammeView
                    header={header}
                    page={code === 'F2' ? f2Content : fapp01Content}
                    refCode={code}
                    values={pageData[code] ?? {}}
                    onChange={(k, v) => handleFieldChange(code, k, v)}
                />
            ) : null
        ) : null,
    })), [header, pageData, handleFieldChange, indexRows, fsContent, f2Content, fapp01Content]);

    if (loading) {
        return <Skeleton variant="rectangular" height={480} sx={{m: 2}}/>;
    }

    if (error && !header) {
        return (
            <Box sx={{p: 2}}>
                <Typography color="error">{error}</Typography>
            </Box>
        );
    }

    const saveDisabled = DISPLAY_ONLY_PAGES.has(pageKey) || !dirty[pageKey] || saving;

    return (
        <Box sx={{height: '100%', minHeight: 0, display: 'flex', flexDirection: 'column'}}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{px: 2, pt: 1, pb: 0.5}}>
                <Typography variant="subtitle1" fontWeight={600}>
                    F Section — Property, plant and equipment
                </Typography>
                <Button
                    size="small"
                    variant="contained"
                    startIcon={<SaveIcon/>}
                    disabled={saveDisabled}
                    onClick={() => void handleSave()}
                >
                    {saving ? 'Saving…' : 'Save'}
                </Button>
            </Stack>
            {error ? (
                <Typography color="error" variant="body2" sx={{px: 2}}>{error}</Typography>
            ) : null}
            <Box sx={{flex: 1, minHeight: 0}}>
                <SplitTabsPanel
                    tabs={tabs}
                    selectedKey={pageKey}
                    onChange={(k) => setPageKey(k as FSectionPageCode)}
                    height="100%"
                    allowMaximize={false}
                    contentWrapperSx={{height: '100%', overflow: 'auto', p: 0}}
                    rightPaneSx={{height: '100%', overflow: 'hidden'}}
                />
            </Box>
        </Box>
    );
}
